import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useSortable } from "../lib/useSortable";
import { toast } from "sonner";
import { Users, ListChecks, Building2, Layers, TrendingUp, ArrowRight } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
} from "recharts";

const GROUP_LABELS = {
    fib: "FIB · Felicidade Interna Bruta",
    whr: "WHR · World Happiness Report",
    global: "Geral",
    custom: "Personalizadas",
};
const GROUP_COLOR = { fib: "#0d1628", whr: "#b89b6f", global: "#52525b", custom: "#8b5cf6" };
const PHASE_LABELS = {
    1: "1 · Avaliar Diagnóstico",
    2: "2 · Projetar Futuro",
    3: "3 · Planear",
    4: "4 · Implementar",
    5: "5 · Avaliar",
};

export default function AdminHappinessStatsPage() {
    const [phase, setPhase] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/happiness/statistics?phase=${phase}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [phase]);

    const indicesByGroup = useMemo(() => {
        if (!data) return {};
        const g = {};
        for (const i of data.indices) {
            if (i.score === null || i.score === undefined) continue;
            (g[i.group || "global"] ||= []).push(i);
        }
        return g;
    }, [data]);

    const companiesBarData = useMemo(
        () => (data?.companies || []).slice(0, 15).map((c) => ({ name: c.company_name, score: c.average_score || 0 })),
        [data?.companies],
    );

    return (
        <div className="space-y-8" data-testid="admin-happiness-stats-page">
            <div>
                <div className="label-caps text-[#52525b]">Super Admin · Visão Global</div>
                <h1 className="font-display text-5xl text-[#0d1628] mt-2">Dashboard de Felicidade</h1>
                <p className="text-[#52525b] mt-3 max-w-3xl">
                    Métricas FIB & WHR agregadas em todas as empresas do ecossistema IHTP, com ranking por empresa e detalhe por índice.
                </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <span className="label-caps text-[#52525b]">Fase</span>
                {[1, 2, 3, 4, 5].map((p) => (
                    <button key={p} type="button" onClick={() => setPhase(p)}
                        data-testid={`phase-tab-${p}`}
                        className={`px-3 py-1.5 text-sm border ${phase === p ? "bg-[#0d1628] text-white border-[#0d1628]" : "bg-white text-[#52525b] border-[#e4e4e7]"}`}>
                        {PHASE_LABELS[p]}
                    </button>
                ))}
            </div>

            {loading || !data ? (
                <div className="label-caps text-[#52525b]" data-testid="global-loading">A carregar estatísticas...</div>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi icon={Building2} label="Empresas com respostas" value={data.total_companies} testId="kpi-companies" />
                        <Kpi icon={Users} label="Respondentes únicos" value={data.respondents} testId="kpi-respondents" />
                        <Kpi icon={ListChecks} label="Respostas totais" value={data.total_responses} testId="kpi-responses" />
                        <Kpi icon={TrendingUp} label="Score médio global" value={globalAvg(data.indices)} suffix="/5" testId="kpi-avg" />
                    </div>

                    {data.total_responses === 0 ? (
                        <div className="border-l-4 border-[#b89b6f] bg-[#fdf8f0] p-6 text-[#52525b]" data-testid="empty-global">
                            Ainda sem respostas para a Fase {phase}. As estatísticas serão atualizadas assim que utilizadores submeterem inquéritos.
                        </div>
                    ) : (
                        <>
                            {/* Radar by group across all companies */}
                            {Object.entries(indicesByGroup).map(([g, items]) => items.length >= 3 && (
                                <section key={g} data-testid={`global-radar-${g}`}>
                                    <div className="label-caps mb-3" style={{ color: GROUP_COLOR[g] }}>{GROUP_LABELS[g] || g}</div>
                                    <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 380 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RadarChart data={items.map((i) => ({ name: i.name, score: i.score }))}>
                                                <PolarGrid stroke="#e4e4e7" />
                                                <PolarAngleAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} />
                                                <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                                                <Radar name="Ecossistema" dataKey="score" stroke={GROUP_COLOR[g]} fill={GROUP_COLOR[g]} fillOpacity={0.35} />
                                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                                <Tooltip contentStyle={{ background: "#0d1628", color: "#fff", border: "none", fontSize: 12 }} />
                                            </RadarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>
                            ))}

                            {/* Companies ranking */}
                            <CompaniesRanking companies={data.companies} phase={phase} />

                            {/* Comparison bar chart per index */}
                            {data.companies.length >= 2 && (
                                <section data-testid="companies-bar">
                                    <div className="label-caps mb-3">Comparativo Score Médio · top empresas</div>
                                    <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: Math.min(80 + data.companies.length * 28, 480) }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={companiesBarData} layout="vertical" margin={{ left: 140 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                                <XAxis type="number" domain={[0, 5]} stroke="#52525b" />
                                                <YAxis type="category" dataKey="name" stroke="#52525b" style={{ fontSize: 11 }} />
                                                <Tooltip contentStyle={{ background: "#0d1628", color: "#fff", border: "none" }} />
                                                <Bar dataKey="score" fill="#b89b6f" radius={[0, 2, 2, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function globalAvg(indices) {
    if (!indices) return "—";
    const scored = indices.filter((i) => i.score !== null && i.score !== undefined).map((i) => i.score);
    if (!scored.length) return "—";
    return (scored.reduce((a, b) => a + b, 0) / scored.length).toFixed(2);
}

function Kpi({ icon: Icon, label, value, suffix, testId }) {
    return (
        <div className="border border-[#e4e4e7] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[#52525b]">
                <Icon size={14} className="text-[#b89b6f]" />
                <div className="label-caps">{label}</div>
            </div>
            <div className="mt-3 font-display text-3xl text-[#0d1628]">{value}{suffix && <span className="text-base text-[#52525b]">{suffix}</span>}</div>
        </div>
    );
}


function SortableTh({ label, k, sortKey, sortDir, onSort, className = "text-left p-3", align = "left" }) {
    const active = sortKey === k;
    const arrow = active ? (sortDir === "asc" ? "↑" : "↓") : "↕";
    const justify = align === "right" ? "justify-end" : "justify-start";
    return (
        <th className={className} data-testid={`sort-${k}`}>
            <button type="button" onClick={() => onSort(k)}
                className={`label-caps w-full inline-flex items-center gap-1 ${justify} ${active ? "text-[#0d1628]" : "text-[#52525b] hover:text-[#0d1628]"}`}>
                {label}
                <span className={`text-[10px] ${active ? "text-[#b89b6f]" : "text-[#a1a1aa]"}`}>{arrow}</span>
            </button>
        </th>
    );
}

function CompaniesRanking({ companies, phase }) {
    const accessors = useMemo(() => ({
        average_score: (r) => r.average_score ?? -1,
    }), []);
    const { items: sortedCompanies, sortKey, sortDir, requestSort } =
        useSortable(companies, "average_score", "desc", accessors);
    return (
        <section data-testid="companies-ranking">
            <div className="flex items-center gap-2 mb-3">
                <Layers size={14} className="text-[#b89b6f]" />
                <div className="label-caps">Ranking de empresas · Fase {phase}</div>
            </div>
            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#f5f3ee] text-[#52525b]">
                        <tr>
                            <th className="text-left p-3 label-caps w-10">#</th>
                            <SortableTh label="Empresa" k="company_name" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Score médio" k="average_score" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-3" align="right" />
                            <SortableTh label="Respondentes" k="respondents" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-3" align="right" />
                            <SortableTh label="Respostas" k="total_responses" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-3" align="right" />
                            <SortableTh label="Snapshots" k="snapshots" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-3" align="right" />
                            <th className="p-3 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e4e4e7]">
                        {sortedCompanies.map((c, idx) => (
                            <tr key={c.company_id} className="hover:bg-[#fdfcf9]" data-testid={`company-row-${c.company_id}`}>
                                <td className="p-3 text-[#b89b6f] font-display text-lg">{idx + 1}</td>
                                <td className="p-3 text-[#0d1628] font-medium">{c.company_name}</td>
                                <td className="p-3 text-right">
                                    {c.average_score !== null ? (
                                        <span className="font-display text-lg text-[#0d1628]">{c.average_score}<span className="text-xs text-[#52525b]">/5</span></span>
                                    ) : <span className="text-[#52525b]">—</span>}
                                </td>
                                <td className="p-3 text-right text-[#52525b]">{c.respondents}</td>
                                <td className="p-3 text-right text-[#52525b]">{c.total_responses}</td>
                                <td className="p-3 text-right text-[#52525b]">{c.snapshots}</td>
                                <td className="p-3 text-right">
                                    <Link to={`/app/super/companies/${c.company_id}`} className="text-[#52525b] hover:text-[#0d1628]" data-testid={`open-company-${c.company_id}`}>
                                        <ArrowRight size={14} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {sortedCompanies.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-[#52525b]">Nenhuma empresa com respostas nesta fase.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
